/**
 * Live rates panel — a streaming ticker + an instant converter, both fed by the
 * `/api/ws/rates/` WebSocket. Prices update as the server ticks them; typing an
 * amount asks the same socket for a quote and shows what you receive AND the
 * itemised charge, so the fee is never hidden inside the price (FR-U-LQD-001).
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { type LiveQuote, useLiveRates } from '@/lib/use-live-rates';

const TICKER = ['USDT', 'USDC', 'BTC', 'ETH', 'USD', 'EUR', 'GBP'];

function fmt(n: string | number, dp = 2) {
  const v = Number(String(n).replace(/,/g, ''));
  if (!isFinite(v)) return String(n);
  return v.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

export function LiveRates({ className }: { className?: string }) {
  const { board, status, requestQuote } = useLiveRates();
  const priceOf = useMemo(() => {
    const m = new Map<string, string>();
    board?.rates.forEach((r) => m.set(r.asset, r.kes_per_unit));
    return m;
  }, [board]);

  const [from, setFrom] = useState('KES');
  const [to, setTo] = useState('USDT');
  const [amount, setAmount] = useState('1000');
  const [quote, setQuote] = useState<LiveQuote | null>(null);
  const [pending, setPending] = useState(false);
  const seq = useRef(0);

  // Debounced instant quote whenever the inputs (or the live board) change.
  useEffect(() => {
    if (!amount || Number(amount) <= 0 || from === to || status !== 'live') {
      setQuote(null);
      return;
    }
    const id = ++seq.current;
    setPending(true);
    const t = setTimeout(async () => {
      const q = await requestQuote(from, to, amount);
      if (id === seq.current) {
        setQuote(q);
        setPending(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [from, to, amount, status, board?.as_of, requestQuote]);

  const assets = board?.rates.map((r) => r.asset) ?? [];
  const dot =
    status === 'live' ? 'bg-accent' : status === 'connecting' ? 'bg-warning' : 'bg-danger';

  return (
    <div className={`rounded-card border border-border bg-surface p-5 shadow-card ${className ?? ''}`}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-semibold text-ink">Live rates</h2>
        <span className="flex items-center gap-1.5 text-xs text-ink-muted">
          <span className={`h-2 w-2 rounded-full ${dot} ${status === 'live' ? 'animate-pulse' : ''}`} />
          {status === 'live' ? 'Streaming' : status === 'connecting' ? 'Connecting…' : 'Offline'}
        </span>
      </div>

      {/* Ticker */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
        {TICKER.filter((a) => priceOf.has(a)).map((a) => (
          <div key={a} className="rounded-lg border border-border bg-canvas px-3 py-2">
            <div className="text-xs text-ink-muted">1 {a}</div>
            <div className="tabular font-mono text-sm font-semibold text-ink">
              {fmt(priceOf.get(a) ?? '0')} <span className="text-xs font-normal text-ink-muted">KES</span>
            </div>
          </div>
        ))}
      </div>

      {/* Instant converter */}
      <div className="mt-4 rounded-lg border border-border bg-canvas p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_1fr]">
          <label className="block">
            <span className="text-xs font-medium text-ink-muted">You pay</span>
            <div className="mt-1 flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2">
              <input
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
                className="w-full bg-transparent font-mono text-sm text-ink outline-none"
                placeholder="0"
              />
              <select
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="bg-transparent text-sm font-medium text-ink outline-none dark:[color-scheme:dark]"
              >
                {assets.map((a) => (
                  <option key={a}>{a}</option>
                ))}
              </select>
            </div>
          </label>

          <div className="flex items-end justify-center pb-2 text-ink-muted">→</div>

          <label className="block">
            <span className="text-xs font-medium text-ink-muted">You receive</span>
            <div className="mt-1 flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2">
              <span className="w-full truncate font-mono text-sm font-semibold text-ink">
                {quote ? fmt(quote.receive, quote.receive_asset === to ? 6 : 2) : pending ? '…' : '—'}
              </span>
              <select
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="bg-transparent text-sm font-medium text-ink outline-none dark:[color-scheme:dark]"
              >
                {assets.map((a) => (
                  <option key={a}>{a}</option>
                ))}
              </select>
            </div>
          </label>
        </div>

        {/* Itemised: rate + charge, always shown so the fee is never hidden */}
        <dl className="mt-3 space-y-1 text-xs">
          <div className="flex justify-between">
            <dt className="text-ink-muted">Rate</dt>
            <dd className="tabular font-mono text-ink">
              {quote ? `1 ${from} = ${fmt(quote.rate, 6)} ${to}` : '—'}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-ink-muted">Charge</dt>
            <dd className="tabular font-mono text-ink">
              {quote ? `${fmt(quote.fee)} ${quote.fee_asset}` : '—'}
            </dd>
          </div>
          <div className="flex justify-between border-t border-border pt-1 font-medium">
            <dt className="text-ink">You receive</dt>
            <dd className="tabular font-mono text-brand">
              {quote ? `${fmt(quote.receive, quote.receive_asset === to ? 6 : 2)} ${to}` : '—'}
            </dd>
          </div>
        </dl>
        <p className="mt-2 text-[11px] text-ink-muted">
          Live simulation — display only. The charge above is the same fee applied at settlement.
        </p>
      </div>
    </div>
  );
}
